import React, { useMemo } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import {
  Button,
  type Intent,
  ExclamationCircleIcon,
  QuestionCircleIcon,
} from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'

import { Dialog } from './dialog'

import type { DialogProps } from './dialog'
import type { DialogVariant } from './dialog-variant'
import type { ClassNameProps } from '@root/shared/react'

// TODO: Wait for designs for actions

export type ConfirmDialogProps = Pick<DialogProps, 'onOpenChange' | 'open'> & {
  variant?: DialogVariant
  withCloseButton?: boolean
  icon?: React.ReactElement | 'question' | 'danger'
  header?: React.ReactNode
  title?: React.ReactNode
  describedBy: string
  description?: React.ReactNode
  onCancel?: () => void
  onConfirm?: () => void
  confirmLabel?: React.ReactNode
  cancelLabel?: React.ReactNode
  intent?: Intent
  busy?: boolean
} & ClassNameProps

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  variant = 'functional',
  open,
  onOpenChange,
  header,
  title,
  describedBy,
  description,
  onCancel,
  onConfirm,
  confirmLabel,
  cancelLabel,
  icon = 'question',
  intent = 'error',
  busy = false,
  className,
}) => {
  const handleClose = () => onOpenChange?.(false)

  const { t } = useTranslation()

  const color = intent === 'error' ? 'errorA' : intent
  const iconEl = useMemo(() => {
    return match(icon)
      .when(React.isValidElement, (icon) => icon)
      .with('danger', () => (
        <ExclamationCircleIcon color="var(--error-9)" size={24} />
      ))
      .otherwise(() => <QuestionCircleIcon size={24} />)
  }, [icon])

  return (
    <Dialog
      variant={variant}
      describedBy={describedBy}
      open={open}
      onOpenChange={handleClose}
      className={className}
    >
      <Dialog.Header title={header ?? title} />
      <Content>
        <Circle data-icon={typeof icon === 'string' ? icon : 'custom'}>
          {iconEl}
        </Circle>
        {title ? <Title>{title}</Title> : undefined}
        {description ? <Description>{description}</Description> : undefined}
        <Actions>
          <Button
            onClick={onCancel}
            size="h28"
            radius="rounded"
            variant="secondary"
            type="button"
          >
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            size="h28"
            radius="rounded"
            variant="glass-2"
            color={color}
            busy={busy}
            type="button"
          >
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </Actions>
      </Content>
    </Dialog>
  )
}

const Content = styled.div`
  width: 320px;
  display: grid;
  justify-items: center;
  justify-self: center;

  padding-inline: var(--confirm-dialog-padding-inline, 12px);
  padding-block: var(--confirm-dialog-padding-block, 16px 26px);
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
`

const Description = styled.div`
  color: var(--text-10);
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  margin-top: 6px;
`

const Circle = styled.div`
  width: 38px;
  height: 38px;
  background: var(--backgroundA-4);
  border-radius: 50%;
  margin-bottom: 8px;
  display: grid;
  align-items: center;
  justify-content: center;

  &[data-icon='danger'] {
    background: var(--errorA-3);
  }
`

const Actions = styled.div`
  margin-top: 12px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 6px;
`
